/**
 * Portfolio — the accounts page.
 *
 * UI sprint step 2 reskin (2026-05-11): rebuilt against pwa-v2's
 * accounts-page pattern.
 *   - `.v2-page wide` container, `.page-head` eyebrow + title + sub
 *   - `.acc-bar` with segmented health filter + sort buttons
 *   - `.acc-grid` of `.acc-card`s with Avatar, HealthPill, v2-chips
 *   - Stale border on the left when an account is past its cadence
 *   - Pop-in cascade on the card grid
 *
 * Known Accounts notes:
 *   - We don't have `last_observation_content` from the entities API yet
 *     (it joins via /api/captures/recent which is tenant-wide, not per-
 *     account). The card's "last touch" line is deferred until the API
 *     enriches the list response.
 *   - Health filter buckets work even when most entities are uncategorized
 *     ("Clients" catches everything not flagged partner/competitor/employer).
 */
import { useEffect, useState } from 'preact/hooks';
import {
  createEntity,
  type EntityListItem,
  listEntities,
} from '../api.ts';
import { Avatar, HealthPill } from '../components/primitives';
import { Icons } from '../components/icons';

interface PortfolioProps {
  refreshKey: number;
  onSelectEntity?: (id: string) => void;
}

type SortKey = 'days' | 'name' | 'tier' | 'health';
type FilterKey = 'client' | 'prospect' | 'partner' | 'all';

interface EnrichedAccount extends EntityListItem {
  days: number | null;
  cadence: number;
  tier: string;
  industry: string;
  health: string;
  nextAction: string;
}

function enrich(a: EntityListItem): EnrichedAccount {
  const days = daysSince(a.last_observation_at);
  return {
    ...a,
    days,
    cadence: numberOr(a.metadata.cadence_days, 30),
    tier: String(a.metadata.tier ?? '').toUpperCase() || '—',
    industry: (a.metadata.industry as string) || '',
    health: (a.metadata.health as string) || 'unknown',
    nextAction: (a.metadata.next_action as string) || '',
  };
}

function filterMatch(a: EnrichedAccount, f: FilterKey): boolean {
  const h = a.health.toLowerCase();
  const excluded = ['partner', 'competitor', 'employer'];
  if (f === 'all') return true;
  if (f === 'partner') return h === 'partner';
  if (f === 'prospect')
    return !excluded.includes(h) && (h === 'prospecting' || h === 'prospect' || h === 'new');
  return !excluded.includes(h);
}

function staleness(days: number | null, cadence: number): 'ok' | 'warn' | 'danger' {
  if (days == null) return 'danger';
  if (days > cadence) return 'danger';
  if (days > cadence * 0.7) return 'warn';
  return 'ok';
}

function stalenessColor(s: 'ok' | 'warn' | 'danger') {
  return s === 'ok' ? 'var(--ok)' : s === 'warn' ? 'var(--warn)' : 'var(--danger)';
}

export function Portfolio({ refreshKey }: PortfolioProps) {
  const [accounts, setAccounts] = useState<EnrichedAccount[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterKey>('client');
  const [sort, setSort] = useState<SortKey>('days');
  const [addOpen, setAddOpen] = useState(false);
  const [reloadCounter, setReloadCounter] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setAccounts(null);
    setError(null);
    listEntities({ type: 'account' })
      .then((a) => {
        if (!cancelled) setAccounts(a.map(enrich));
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey, reloadCounter]);

  const filtered = (accounts ?? []).filter((a) => filterMatch(a, filter));
  const sorted = [...filtered].sort((a, b) => {
    switch (sort) {
      case 'days':
        return (b.days ?? -1) - (a.days ?? -1);
      case 'name':
        return a.name.localeCompare(b.name);
      case 'tier':
        return a.tier.localeCompare(b.tier);
      case 'health':
        return a.health.localeCompare(b.health);
    }
  });

  const counts = accounts
    ? {
        client: accounts.filter((a) => filterMatch(a, 'client')).length,
        prospect: accounts.filter((a) => filterMatch(a, 'prospect')).length,
        partner: accounts.filter((a) => filterMatch(a, 'partner')).length,
        all: accounts.length,
      }
    : { client: 0, prospect: 0, partner: 0, all: 0 };

  const atRisk = accounts
    ? accounts.filter((a) => staleness(a.days, a.cadence) === 'danger').length
    : 0;

  return (
    <div class="v2-page wide">
      <div class="page-head">
        <div>
          <div class="page-eyebrow">
            Portfolio · {accounts ? `${filtered.length} accounts` : 'loading'}
          </div>
          <h1 class="v2-page-title">Accounts</h1>
          {accounts && accounts.length > 0 && (
            <div class="page-sub">
              {atRisk} past cadence · {counts.prospect} prospects
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            class="btn btn-primary"
            onClick={() => setAddOpen((v) => !v)}
          >
            <Icons.Plus size={14} />
            {addOpen ? 'Cancel' : 'Add account'}
          </button>
        </div>
      </div>

      {error && <div class="error-banner">{error}</div>}

      {addOpen && (
        <AddAccountForm
          onSubmit={async (name, industry) => {
            await createEntity({
              type: 'account',
              name,
              metadata: industry ? { industry } : {},
            });
            setAddOpen(false);
            setReloadCounter((c) => c + 1);
          }}
        />
      )}

      <div class="acc-bar">
        <div class="seg">
          {(['client', 'prospect', 'partner', 'all'] as FilterKey[]).map((k) => (
            <button
              key={k}
              class={filter === k ? 'active' : ''}
              onClick={() => setFilter(k)}
            >
              {k === 'client'
                ? 'Clients'
                : k === 'prospect'
                  ? 'Prospects'
                  : k === 'partner'
                    ? 'Partners'
                    : 'All'}
              <span class="seg-count">{counts[k]}</span>
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <div class="acc-sort">
          <span style={{ color: 'var(--text-muted)', fontSize: 11.5 }}>Sort</span>
          {(['days', 'name', 'tier', 'health'] as SortKey[]).map((k) => (
            <button
              key={k}
              class={`btn btn-ghost ${sort === k ? 'sort-active' : ''}`}
              onClick={() => setSort(k)}
            >
              {k === 'days' ? 'Staleness' : k.charAt(0).toUpperCase() + k.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {accounts === null ? (
        <div class="acc-grid">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} class="acc-card skel" style={{ height: 112 }} />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <div
          class="panel"
          style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}
        >
          {accounts.length === 0
            ? 'No accounts yet. Add one above or capture something to populate your portfolio.'
            : 'No accounts match this filter.'}
        </div>
      ) : (
        <div class="acc-grid pop">
          {sorted.map((a) => (
            <AccountCard key={a.id} a={a} />
          ))}
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Card
// ───────────────────────────────────────────────────────────────────────────

function AccountCard({ a }: { a: EnrichedAccount }) {
  const healthLower = a.health.toLowerCase();
  const trackStaleness = !['competitor', 'employer'].includes(healthLower);
  const s = trackStaleness ? staleness(a.days, a.cadence) : 'ok';
  const color = stalenessColor(s);

  return (
    <a
      href={`/entity/${a.id}`}
      class={`acc-card ${trackStaleness && s === 'danger' ? 'stale' : ''}`}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <div class="acc-card-head">
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0, flex: 1 }}>
          <Avatar name={a.name} size={26} />
          <span class="acc-card-name truncate">{a.name}</span>
        </div>
        {a.health !== 'unknown' && <HealthPill health={a.health} />}
      </div>
      {a.industry && <div class="acc-card-industry">{a.industry}</div>}
      <div class="acc-card-foot">
        <div
          style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}
        >
          {trackStaleness && (
            <span class="v2-chip" style={{ color }}>
              <Icons.Clock size={10} />
              <span class="mono">{a.days != null ? `${a.days}d` : '—'}</span>
            </span>
          )}
          {a.tier !== '—' && (
            <span class="v2-chip">
              <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>tier</span>
              <span
                class="mono"
                style={{ color: a.tier === 'A' ? 'var(--accent)' : 'var(--text-2)' }}
              >
                {a.tier}
              </span>
            </span>
          )}
          <span class="v2-chip" style={{ color: 'var(--text-muted)' }}>
            <span class="mono">{a.observation_count}</span>
            <span style={{ color: 'var(--text-faint)', fontSize: 10 }}>obs</span>
          </span>
        </div>
      </div>
      {a.nextAction && <div class="acc-card-next truncate">→ {a.nextAction}</div>}
    </a>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Add-account form
// ───────────────────────────────────────────────────────────────────────────

function AddAccountForm({
  onSubmit,
}: { onSubmit: (name: string, industry: string | null) => Promise<void> }) {
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: Event) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(name.trim(), industry.trim() || null);
      setName('');
      setIndustry('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} class="panel" style={{ marginBottom: 16, padding: 16 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input
          type="text"
          class="input"
          placeholder="Account name (required)"
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          autoFocus
        />
        <input
          type="text"
          class="input"
          placeholder="Industry (optional)"
          value={industry}
          onInput={(e) => setIndustry((e.target as HTMLInputElement).value)}
        />
        <div>
          <button
            type="submit"
            class="btn btn-primary"
            disabled={!name.trim() || submitting}
          >
            {submitting ? 'Adding…' : 'Add account'}
          </button>
        </div>
        {error && <div class="error-banner">{error}</div>}
      </div>
    </form>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.floor((Date.now() - then) / 86_400_000);
}

function numberOr(v: unknown, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}
